import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Edit, Trash2, ArrowLeft, Calendar, DollarSign, User, FileText, ChevronDown } from 'lucide-react';
import { maintenanceApi } from '@/api/maintenance';
import type { Maintenance, MaintenanceStatus } from '@/types';
import LoadingSpinner from '@/components/LoadingSpinner';
import ConfirmDialog from '@/components/ConfirmDialog';
import { formatDate, formatCurrency, formatStatus, getStatusColor, getPriorityColor } from '@/utils/formatters';

const typeStyles: Record<string, string> = {
  preventive: 'bg-blue-100 text-blue-700',
  corrective: 'bg-orange-100 text-orange-700',
};

const typeLabels: Record<string, string> = {
  preventive: 'Preventivo',
  corrective: 'Correctivo',
};

const statusTransitions: Record<string, { next: MaintenanceStatus; label: string }> = {
  pending: { next: 'in-progress' as MaintenanceStatus, label: 'Iniciar' },
  'in-progress': { next: 'completed' as MaintenanceStatus, label: 'Completar' },
  completed: { next: 'cancelled' as MaintenanceStatus, label: 'Cancelar' },
};

const priorityLabels: Record<string, string> = {
  low: 'Baja',
  medium: 'Media',
  normal: 'Normal',
  high: 'Alta',
  critical: 'Crítica',
};

export function MaintenanceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDelete, setShowDelete] = useState(false);

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['maintenance', id],
    queryFn: () => maintenanceApi.getById(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => maintenanceApi.remove(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      toast.success('Mantenimiento eliminado exitosamente');
      navigate('/maintenance');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al eliminar mantenimiento');
    },
  });

  const statusMutation = useMutation({
    mutationFn: (newStatus: MaintenanceStatus) => maintenanceApi.updateStatus(id!, newStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance', id] });
      toast.success('Estado actualizado exitosamente');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al actualizar estado');
    },
  });

  const handleStatusChange = (nextStatus: MaintenanceStatus) => {
    statusMutation.mutate(nextStatus);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !response?.data?.data) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-danger-200 bg-danger-50 p-8 text-center">
        <p className="text-sm font-medium text-danger-700">Mantenimiento no encontrado</p>
        <Link
          to="/maintenance"
          className="mt-3 rounded-md bg-danger-100 px-3 py-1.5 text-sm font-medium text-danger-700 hover:bg-danger-200 transition-colors"
        >
          Volver a mantenimientos
        </Link>
      </div>
    );
  }

  const maintenance = response.data.data as Maintenance & {
    type?: 'preventive' | 'corrective';
    cctvId?: string;
    technician?: string;
    cost?: number;
    assignedUser?: { name: string };
    assignedTo?: string;
    createdBy?: { name: string };
  };

  const normalizedStatus = maintenance.status.replace(/_/g, '-');
  const transition = statusTransitions[normalizedStatus];
  const canChangeStatus = !!transition;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/maintenance')}
            className="rounded-md p-2 text-surface-400 hover:bg-surface-100 hover:text-surface-600 transition-colors"
            aria-label="Volver"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-surface-900">
            {typeLabels[maintenance.type || ''] || 'Mantenimiento'} {maintenance.client?.name ? `- ${maintenance.client.name}` : ''}
          </h1>
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(normalizedStatus)}`}>
            {formatStatus(normalizedStatus)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canChangeStatus && (
            <button
              type="button"
              onClick={() => handleStatusChange(transition!.next)}
              disabled={statusMutation.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
            >
              {statusMutation.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              {transition!.label}
            </button>
          )}
          <Link
            to={`/maintenance/${id}/edit`}
            className="inline-flex items-center gap-2 rounded-md border border-surface-300 bg-white px-3 py-2 text-sm font-medium text-surface-700 shadow-sm hover:bg-surface-50 transition-colors"
          >
            <Edit className="h-4 w-4" />
            Editar
          </Link>
          <button
            type="button"
            onClick={() => setShowDelete(true)}
            className="inline-flex items-center gap-2 rounded-md border border-danger-300 bg-white px-3 py-2 text-sm font-medium text-danger-700 shadow-sm hover:bg-danger-50 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Eliminar
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-surface-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-surface-900">Información General</h2>
          <dl className="space-y-3">
            <div className="flex items-start gap-3">
              <User className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Cliente</dt>
                <dd className="text-sm text-surface-900">{maintenance.client?.name || '-'}</dd>
              </div>
            </div>
            {maintenance.type && (
              <div className="flex items-start gap-3">
                <FileText className="mt-0.5 h-4 w-4 text-surface-400" />
                <div>
                  <dt className="text-xs font-medium text-surface-500">Tipo</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${typeStyles[maintenance.type] || 'bg-surface-100 text-surface-700'}`}>
                      {typeLabels[maintenance.type] || maintenance.type}
                    </span>
                  </dd>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <FileText className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Prioridad</dt>
                <dd className="mt-1">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getPriorityColor(maintenance.priority)}`}>
                    {priorityLabels[maintenance.priority] || formatStatus(maintenance.priority)}
                  </span>
                </dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FileText className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Estado</dt>
                <dd className="mt-1">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(normalizedStatus)}`}>
                    {formatStatus(normalizedStatus)}
                  </span>
                </dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FileText className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Descripción</dt>
                <dd className="text-sm text-surface-900 whitespace-pre-wrap">{maintenance.description}</dd>
              </div>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-surface-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-surface-900">Detalles</h2>
          <dl className="space-y-3">
            <div className="flex items-start gap-3">
              <Calendar className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Fecha Programada</dt>
                <dd className="text-sm text-surface-900">
                  {maintenance.scheduledDate ? formatDate(maintenance.scheduledDate) : 'No programada'}
                </dd>
              </div>
            </div>
            {maintenance.completedDate && (
              <div className="flex items-start gap-3">
                <Calendar className="mt-0.5 h-4 w-4 text-surface-400" />
                <div>
                  <dt className="text-xs font-medium text-surface-500">Fecha de Completado</dt>
                  <dd className="text-sm text-surface-900">{formatDate(maintenance.completedDate)}</dd>
                </div>
              </div>
            )}
            {maintenance.technician && (
              <div className="flex items-start gap-3">
                <User className="mt-0.5 h-4 w-4 text-surface-400" />
                <div>
                  <dt className="text-xs font-medium text-surface-500">Técnico</dt>
                  <dd className="text-sm text-surface-900">{maintenance.technician}</dd>
                </div>
              </div>
            )}
            {maintenance.assignedUser && (
              <div className="flex items-start gap-3">
                <User className="mt-0.5 h-4 w-4 text-surface-400" />
                <div>
                  <dt className="text-xs font-medium text-surface-500">Asignado a</dt>
                  <dd className="text-sm text-surface-900">{maintenance.assignedUser.name}</dd>
                </div>
              </div>
            )}
            {maintenance.assignedTo && !maintenance.assignedUser && (
              <div className="flex items-start gap-3">
                <User className="mt-0.5 h-4 w-4 text-surface-400" />
                <div>
                  <dt className="text-xs font-medium text-surface-500">Asignado a</dt>
                  <dd className="text-sm text-surface-900">{maintenance.assignedTo}</dd>
                </div>
              </div>
            )}
            {maintenance.cost != null && (
              <div className="flex items-start gap-3">
                <DollarSign className="mt-0.5 h-4 w-4 text-surface-400" />
                <div>
                  <dt className="text-xs font-medium text-surface-500">Costo</dt>
                  <dd className="text-sm text-surface-900 font-medium">{formatCurrency(maintenance.cost)}</dd>
                </div>
              </div>
            )}
            {maintenance.createdBy && (
              <div className="flex items-start gap-3">
                <User className="mt-0.5 h-4 w-4 text-surface-400" />
                <div>
                  <dt className="text-xs font-medium text-surface-500">Creado por</dt>
                  <dd className="text-sm text-surface-900">{maintenance.createdBy.name}</dd>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <Calendar className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Creado el</dt>
                <dd className="text-sm text-surface-900">{formatDate(maintenance.createdAt)}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Actualizado el</dt>
                <dd className="text-sm text-surface-900">{formatDate(maintenance.updatedAt)}</dd>
              </div>
            </div>
          </dl>
        </div>
      </div>

      {maintenance.notes && (
        <div className="rounded-lg border border-surface-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-surface-900">
            <FileText className="h-5 w-5 text-surface-400" />
            Notas
          </h2>
          <p className="text-sm text-surface-700 whitespace-pre-wrap">{maintenance.notes}</p>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={() => {
          deleteMutation.mutate();
          setShowDelete(false);
        }}
        title="Eliminar Mantenimiento"
        message={`¿Estás seguro de eliminar este mantenimiento? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}

export default MaintenanceDetail;
